export default () => ({
    opened: false,
    column: null,
    watermark: true,
    canDownload: false,
    canFavorite: false,
    canSelect: false,
    quickBuy: false,
    urlParams: {},
    activeImage: null,
    photos: null,
    selectedIndex: null,
    quantity: 0,
    event: null,
    swiper: null,
    windowHeight: null,
    checkImages: true,
    downloading: false,
    hasClientRole: false,

    imagesSelected: {},
    packages: [],
    perPage: 0,
    lastLoadedOffset: 0,
    requestedOffsets: [],
    isLoadingImages: false,
    route: null,
    totalImagesCount: 0,
    previousActiveIndex: null,
    lastForwardOffset: 0,
    lastBackwardOffset: 0,
    firstLoad: true,
    currentEventId: null,

    init(){
        window.addEventListener('resize', () => {
            this.calcContainerHeight();
        });
    },

    set([photos, position, {event='quick-buy-preview::toggle', ...options}]) {
        this.opened = true;
        this.event  = event;
        this.photos = Object.assign({}, photos);
        this.windowHeight = null;

        this.column           = _.get(this.options, 'imageAttr', '');
        this.canDownload      = _.get(options, 'canDownload', true);
        this.canFavorite      = _.get(options, 'canFavorite', false);
        this.checkImages      = _.get(options, 'checkImages', true);
        this.imagesSelected   = _.get(options, 'selected', {});
        this.canSelect        = _.get(options, 'canSelect', false);
        this.quickBuy         = _.get(options, 'quickBuy', false);
        this.urlParams        = _.get(options, 'urlParams', {});
        this.quantity         = _.get(options, 'quantity', 0);
        this.perPage          = this.perPage ? this.perPage : _.get(options, 'perPage', 50);
        this.hasClientRole    = _.get(options, 'hasClientRole', false);
        this.route            = _.get(options, 'route', 'customer');
        this.totalImagesCount = _.get(options, 'totalImagesCount', 0);
        this.currentEventId   = _.get(options, 'eventId', null);

        this.watermark  = _.get(options, 'watermark') ?? this.$store.cart.showWatermark();
        this.previousActiveIndex = position;

        this.$nextTick(() => {
            this.calcContainerHeight();
            this.appendSlides(position);
        });
    },

    calcContainerHeight(){
        this.windowHeight = window.innerHeight;

        document.getElementById('swiper-quick-buy-preview')
            .classList.add('bg-gf-gray-950', 'flex-1', 'flex', 'flex-col', 'h-full', 'w-full');
    },

    appendSlides(position) {
        if(this.swiper) {
            this.swiper.virtual.removeAllSlides();
        } else {
            this.swiperInitialize(position);
        }

        let slides = [];

        Object.values(this.photos).forEach((photo) => {
            const column = this.watermark ? 'watermark_preview' : this.column;

            const preview = _.get(photo, column);

            slides.push(`
                <swiper-slide
                    id='quick-buy-preview-box-${photo.id}'
                    class='h-full w-full flex items-end bg-contain bg-no-repeat bg-center'
                    style="background-image: url('${preview}')"
                >
                    <div class="bg-white px-2 py-1 text-xs" x-text="'${photo?.name || 'loading...'}'"></div>
                </swiper-slide>
            `);
        });

        this.swiper.virtual.appendSlide(slides);
        this.swiper.slideTo(position, 1);

        this.setActiveImage();
    },

    swiperInitialize() {
        if(this.swiper) {
            return;
        }

        const swiperEl = document.getElementById('swiper-quick-buy-preview');
        swiperEl.initialize();

        this.swiper = swiperEl.swiper;

        this.swiper.on('activeIndexChange', async (event) => {
            const swiper = await event;
            const currentIndex = swiper.activeIndex;

            if (this.previousActiveIndex === currentIndex) {
                return;
            }

            const isGoingForward = this.previousActiveIndex < currentIndex;
            const forwardOffset   = !this.lastForwardOffset ? this.lastLoadedOffset : this.lastForwardOffset;
            const backwardOffset = !this.lastBackwardOffset ? this.lastLoadedOffset : this.lastBackwardOffset;

            if (isGoingForward && currentIndex + (this.perPage / 2) < forwardOffset) {
                this.setActiveImage();

                this.previousActiveIndex = currentIndex;

                return;
            }

            if (!isGoingForward && currentIndex - (this.perPage / 2) > backwardOffset) {
                this.setActiveImage();

                this.previousActiveIndex = currentIndex;

                return;
            }

            if (this.isLoadingImages) {
                return;
            }

            const currentOffset = isGoingForward
                ? forwardOffset + (this.firstLoad ? 0 : this.perPage)
                : Math.max(0, backwardOffset - this.perPage);

            const currentOffsetKey = isGoingForward ? `forward-${currentOffset}` : `backward-${currentOffset}`;

            if (this.requestedOffsets.includes(currentOffsetKey) && !this.firstLoad) {
                this.setActiveImage();

                this.previousActiveIndex = currentIndex;

                return;
            }

            this.firstLoad = false;
            this.isLoadingImages = true;
            this.previousActiveIndex = currentIndex;

            try {
                const newImages = await this.loadMoreImages(currentOffset, currentIndex);
                this.photos = { ...this.photos, ...newImages };

                if (!this.requestedOffsets.includes(currentOffsetKey)) {
                    this.requestedOffsets.push(currentOffsetKey);
                }

                this.lastLoadedOffset = currentOffset;

                if (isGoingForward) {
                    this.lastForwardOffset = currentOffset;
                } else {
                    this.lastBackwardOffset = currentOffset;
                }

                this.appendSlides(currentIndex);
            } finally {
                this.isLoadingImages = false;
            }
        });
    },

    setActiveImage() {
        const activeImageItem = Object.entries(this.photos)[this.swiper.activeIndex];
    
        this.activeImage   = activeImageItem[1];
        this.selectedIndex = activeImageItem[0];
    },

    async loadMoreImages(offset = 0, currentIndex) {
        const params = {};

        const image = this.photos[currentIndex];
        if (!image) return {};

        if (this.route === 'event') {
            params.eventId = image?.event_id ?? this.currentEventId;
        } else if (this.route === 'customer') {
            params.mainEventId = image.main_event_id;
        }

        params.offset = offset;
        params.limit = this.perPage;

        const response = await fetch(`/${this.route}/images?` + new URLSearchParams(params));

        if (!response.ok) {
            throw new Error(`Failed to load images: ${response.status}`);
        }

        const { data } = await response.json();
        const merged = {};

        data.forEach((photo, i) => {
            merged[offset + i] = photo;
        });

        return merged;
    },

    updateRequestData({ offsets, limit }) {
        this.perPage = limit;

        const mergedOffsets = new Set([...this.requestedOffsets, ...offsets]);
        this.requestedOffsets = [...mergedOffsets];
        this.lastLoadedOffset = this.requestedOffsets[this.requestedOffsets.length - 1] || limit;
    },

    addToPackage() {
        this.$dispatch('quick-buy-preview::add-to-package', this.selectedIndex);

        this.close();
    },

    close() {
        this.opened = false;
    },

    update(image=null, action=null) {
        if(this.event === null) {
            return;
        }

        this.$dispatch(this.event, { image, action });
    },

    checkImage(element, photoId) {
        element.onload = () => document.querySelectorAll(`[data-loading=loading-${photoId}]`)?.forEach(
            element => element.remove()
        );

        if(element.complete) {
            element.onload()
        }
    },
})
